import { makeStyles } from "@material-ui/core/styles";
import { dataProvider } from "api";
import CodeDialog from "components/CodeDialog";
import { AppList } from "components/list/AppList";
import { PalletReservationState } from "entities/PalletReservationState";
import React, { useMemo, useRef, useState } from "react";
import { DataProviderContext, useTranslate } from "react-admin";
import { useSelector } from "react-redux";

import { PickingListActions } from "./components/Actions";
import { PickingListFilter } from "./components/Filters";
import { operationModeToggleEnum } from "./components/OperationModeToggleFilter";
import { useBoxMovement } from "./hooks/useBoxMovement";
import { usePickingListReservation } from "./hooks/usePickingListReservation";
import { PickingListGrid } from "./PickingListGrid";

const useStyles = makeStyles({
	listRoot: {
		"& .MuiToolbar-root": {
			display: "flex",
			alignItems: "center",
			paddingBottom: "8px",
		},
		"& .MuiCard-root": {
			width: "100%",
		},
	},
});

export const sortDefaultValue = { field: "sscc", order: "DESC" };
const operationDefaultStateValue = operationModeToggleEnum[1].value;
const rightSideOffset = 0;

export const PickingList = ({ orderId, terminalId, orderPallets, orderStatus }) => {
	const translate = useTranslate();
	const classes = useStyles();

	const [isReservationMode, setIsReservationMode] = React.useState(false);
	const [isSelectedViewMode, setIsSelectedViewMode] = React.useState(false);
	const [operationMode, setOperationMode] = React.useState(operationDefaultStateValue);
	const actionOnSelectionPallet = useRef(() => null);
	const actionOnBoxMovementPick = useRef(() => null);

	const isSidebarOpen = useSelector((state) => state.admin.ui.sidebarOpen);

	// Barcode dialog
	const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
	const [barcodeValue, setBarcodeValue] = useState("");
	const [barcodeTitle, setBarcodeTitle] = useState("");

	// Use reservation hook
	const reservation = usePickingListReservation({ orderId });

	// Use box movement hook
	const boxMovement = useBoxMovement();

	const wasInBoxMovementRef = useRef(false);
	React.useEffect(() => {
		const inBoxMovement = boxMovement.isBoxMovementMode || boxMovement.isSelectingTarget;
		if (wasInBoxMovementRef.current && !inBoxMovement) {
			setOperationMode(operationDefaultStateValue);
		}
		wasInBoxMovementRef.current = inBoxMovement;
	}, [boxMovement.isBoxMovementMode, boxMovement.isSelectingTarget]);

	const clearData = () => {
		reservation.clearAllSelections();
		setIsSelectedViewMode(false);
		setOperationMode(operationDefaultStateValue);
	};

	const onReserve = () => {
		const newMode = !isReservationMode;
		setIsReservationMode(newMode);
		if (newMode === false) {
			// Clear selections when disabling reserve mode
			clearData();
		}
	};

	const filterDefaultValues = { reservation_state: PalletReservationState.RESERVED };

	// Info: Custom dataprovider for list because we need url with parameter orderId
	const pickingListDataProvider = useMemo(
		() => ({
			...dataProvider,
			getList: (resource, params) => {
				if (resource == "pallets-and-boxes") {
					return dataProvider.getList(`order/${orderId}/pallets-and-boxes`, params);
				}
				return dataProvider.getList(resource, params);
			},
			create: (resource, params) => {
				if (resource === "pallet-box-reservations") {
					return dataProvider.create(`order/${orderId}/pallet-box-reservations`, params);
				}
				if (resource === "automatic-pallets-reservation") {
					return dataProvider.create(`order/${orderId}/automatic-pallets-reservation`, params);
				}
				return dataProvider.create(resource, params);
			},
		}),
		[dataProvider, orderId]
	);

	return (
		<DataProviderContext.Provider value={pickingListDataProvider}>
			<AppList
				style={{ marginRight: rightSideOffset }}
				basePath={`/order/${orderId}/pallets-and-boxes`}
				resource="pallets-and-boxes"
				sort={sortDefaultValue}
				bulkActionButtons={false}
				exporter={false}
				className={classes.listRoot}
				title={`${translate("tab.pallets.pallets_for_order")} #${orderId}`}
				filters={
					<PickingListFilter
						translate={translate}
						isReservationMode={isReservationMode}
						isBoxMovementMode={boxMovement.isBoxMovementMode}
						isSelectedViewMode={isSelectedViewMode}
						setIsSelectedViewMode={setIsSelectedViewMode}
						operationMode={operationMode}
						setOperationMode={setOperationMode}
						filterDefaultValues={filterDefaultValues}
						orderId={orderId}
						orderStatus={orderStatus}
					/>
				}
				actions={
					<PickingListActions
						onReserve={onReserve}
						translate={translate}
						orderId={orderId}
						selectedPalletsForReservation={reservation.selectedPalletsForBackend}
						unselectedReservedPallets={reservation.unselectedReservedPallets}
						palletBoxCounts={reservation.palletBoxCounts}
						isReservationMode={isReservationMode}
						isSelectedViewMode={isSelectedViewMode}
						actionOnSelectionPallet={actionOnSelectionPallet}
						actionOnBoxMovementPick={actionOnBoxMovementPick}
						operationMode={operationMode}
						terminalId={terminalId}
						orderPallets={orderPallets}
						orderStatus={orderStatus}
						startBoxMovement={boxMovement.startBoxMovement}
						isSelectingTarget={boxMovement.isSelectingTarget}
						isBoxMovementMode={boxMovement.isBoxMovementMode}
						isConfirmBoxMovementMode={boxMovement.isConfirmBoxMovementMode}
						cancelBoxMovement={boxMovement.cancelBoxMovement}
						clearSelections={reservation.clearBackendSelections}
						refreshReservationTotals={reservation.fetchReservationTotals}
					/>
				}
				filterDefaultValues={filterDefaultValues}
			>
				<PickingListGrid
					isSelectedViewMode={isSelectedViewMode}
					isReservationMode={isReservationMode}
					reservation={reservation}
					boxMovement={boxMovement}
					operationMode={operationMode}
					orderId={orderId}
					actionOnSelectionPallet={actionOnSelectionPallet}
					actionOnBoxMovementPick={actionOnBoxMovementPick}
					terminalId={terminalId}
					setBarcodeValue={setBarcodeValue}
					setBarcodeDialogOpen={setBarcodeDialogOpen}
					setBarcodeTitle={setBarcodeTitle}
					orderStatus={orderStatus}
				/>
			</AppList>
			{/* Barcode Dialog */}
			<CodeDialog
				open={barcodeDialogOpen}
				type="barcode"
				onClose={() => setBarcodeDialogOpen(false)}
				value={barcodeValue}
				title={barcodeTitle || "SSCC Barcode"}
			/>
		</DataProviderContext.Provider>
	);
};
